import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FileText,
  Trash2,
  BookOpen,
  Database,
  FileCheck,
  MoreHorizontal,
  Eye,
  Download,
  DatabaseZap,
  Unlink,
  Loader2,
  RefreshCw,
  CloudUpload,
  EyeOff,
  HardDrive,
  Files,
  FileSpreadsheet,
  FileImage,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cloudSourceService, type CloudSourceListRow } from '@/services/cloudSourceService';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  knowledgeService,
  documentService,
  type ApprovedDocument,
} from '@/services/kbService';
import { Checkbox } from '@/components/ui/checkbox';
import ApprovedDocumentModal from '@/components/knowledge/ApprovedDocumentModal';
import AddToKnowledgeBaseDialog from '@/components/knowledge/AddToKnowledgeBaseDialog';
import ComplianceBadge from '@/components/knowledge/ComplianceBadge';
import { formatFileSize, formatDate } from '@/utils/kbUtils';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { getCsrfToken } from '@/utils/csrf';
import DeleteConfirmDialog from '@/components/knowledge/DeleteConfirmDialog';
import DataTablePagination from '@/components/knowledge/DataTablePagination';
import SortableTableHeader, { useSortableTable } from '@/components/knowledge/SortableTableHeader';
import RefreshButton from '@/components/knowledge/RefreshButton';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useKBFilters } from '@/hooks/useKBFilters';
import KBSearchAndFilters from '@/components/knowledge/KBSearchAndFilters';
import { FILE_TYPE_OPTIONS } from '@/components/knowledge/KBFilterPanel';

import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';
type SortColumn = 'file_name' | 'file_size' | 'entity_count' | 'approved_at';


const isCloudSourceDoc = (doc: ApprovedDocument): boolean =>
  Boolean(doc.cloud_source) ||
  doc.source === 'Google Drive' ||
  doc.source === 'SharePoint';

// Pull the friendly message out of a Frappe error response.
const extractFrappeError = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  if (data?._server_messages) {
    try {
      const msgs = JSON.parse(data._server_messages);
      const parsed = JSON.parse(msgs[0]);
      if (parsed?.message) return String(parsed.message);
    } catch { /* fall through */ }
  }
  if (data?.exception && typeof data.exception === 'string') {
    const tail = data.exception.split(':').slice(1).join(':').trim();
    if (tail) return tail;
  }
  return err?.message || fallback;
};

/** File-type icon for the Asset Identity column */
const getFileIcon = (fileType?: string) => {
  const t = (fileType || '').toLowerCase();
  if (['csv', 'xls', 'xlsx'].includes(t)) return <FileSpreadsheet className="h-4 w-4 text-emerald-400" />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(t)) return <FileImage className="h-4 w-4 text-purple-400" />;
  if (t === 'pdf') return <FileText className="h-4 w-4 text-red-400" />;
  return <FileText className="h-4 w-4 text-blue-400" />;
};

const KBDocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Data state
  const [documents, setDocuments] = useState<ApprovedDocument[]>([]);
  // Monotonic id of the newest in-flight loadDocuments() call; older responses
  // that resolve late are discarded rather than clobbering fresher results.
  const latestRequestRef = useRef(0);
  const filters = useKBFilters();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;
  const [totalDocuments, setTotalDocuments] = useState(0);

  // Sorting state (using shared hook)
  const { sortColumn, sortDirection, handleSort } = useSortableTable<SortColumn>();

  // Modal state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Delete confirmation state
  const [deleteDocumentId, setDeleteDocumentId] = useState<string | null>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Selection state for bulk actions
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [addToKBDialogOpen, setAddToKBDialogOpen] = useState(false);
  const [removeFromKBConfirmVisible, setRemoveFromKBConfirmVisible] = useState(false);
  const [bulkLinking, setBulkLinking] = useState(false);
  const [bulkUnlinking, setBulkUnlinking] = useState(false);
  const [bulkDeleteConfirmVisible, setBulkDeleteConfirmVisible] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // File library stats
  const [libraryStats, setLibraryStats] = useState({
    totalFiles: 0,
    inKnowledgeBase: 0,
    notInKnowledgeBase: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);

  // Connected cloud sources drive the per-provider Sync buttons
  const [cloudSources, setCloudSources] = useState<CloudSourceListRow[]>([]);
  const [syncingSource, setSyncingSource] = useState<string | null>(null);

  const { searchQuery, fileTypeFilter, documentTypeFilter, kbCategorized, sourceFilter, dateRange, handleFileTypeChange, filteredFileTypeOptions } = filters;

  // The search box stays bound to `searchQuery` so typing feels instant, but
  // the fetch runs off this debounced copy - otherwise every keystroke fired a
  // backend request.
  const debouncedSearch = useDebouncedValue(searchQuery, 400);

  useEffect(() => {
    loadCloudSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear selection when filters/search/page change so bulk actions only affect visible data
  useEffect(() => {
    setSelectedDocIds(new Set());
  }, [debouncedSearch, fileTypeFilter, documentTypeFilter, kbCategorized, sourceFilter, dateRange, currentPage]);

  // Load filtered documents when filters/search/page change. This also
  // populates the metric cards (see loadDocuments), so there's no separate
  // unfiltered stats fetch - the cards always reflect the active filters.
  useEffect(() => {
    loadDocuments();
  }, [debouncedSearch, fileTypeFilter, documentTypeFilter, kbCategorized, sourceFilter, dateRange, currentPage]);

  const loadDocuments = async (silent = false) => {
    // Responses can land out of order (an earlier, slower query resolving after
    // a later one), which would show results for a query the user has already
    // moved on from. Only the newest request is allowed to write state.
    const requestId = ++latestRequestRef.current;
    if (!silent) setLoading(true);
    try {
      const params: Record<string, any> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (fileTypeFilter && fileTypeFilter !== 'all') {
        params.file_type = fileTypeFilter;
      }
      if (documentTypeFilter && documentTypeFilter !== 'all') {
        params.document_type = documentTypeFilter;
      }
      if (kbCategorized && kbCategorized !== 'all') {
        params.knowledge_base_categorized = kbCategorized === 'true';
      }
      if (sourceFilter && sourceFilter !== 'all') {
        // Backend accepts 'manual' | 'google' | 'sharepoint'; the SQL layer
        // maps these onto Orgain Document.cloud_source / KB Cloud Folder
        // Source.provider in approved_documents_service.
        params.source = sourceFilter;
      }
      if (dateRange?.from) {
        const d = dateRange.from;
        params.approved_from = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
      }
      if (dateRange?.to) {
        const d = dateRange.to;
        params.approved_till = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
      }

      const response = await knowledgeService.getApprovedDocuments(params);
      if (requestId !== latestRequestRef.current) return; // superseded

      setDocuments(response.data);
      // Every count reflects the active filters, including the in-KB/hidden
      // split. `total` drives both pagination ("Showing X of Y") and the "Total
      // Files" card; with/without drive the other two cards. with + without ==
      // total, so under a KB filter one card is naturally 0 (e.g. "In Knowledge
      // Base" -> Total N / In KB N / Hidden 0).
      setTotalDocuments(response.total);
      setLibraryStats({
        totalFiles: response.total,
        inKnowledgeBase: response.with_knowledge_base,
        notInKnowledgeBase: response.without_knowledge_base,
      });
      setTotalSize(response.total_size || 0);
    } catch (error: any) {
      if (requestId !== latestRequestRef.current) return; // superseded
      logger.error('Failed to load documents:', error);
      toast.error('Failed to load approved documents');
    } finally {
      // A superseded request must not clear the spinner - the newer request it
      // was replaced by is still in flight.
      if (requestId === latestRequestRef.current) {
        if (!silent) setLoading(false);
        setStatsLoading(false);
      }
    }
  };

  const loadCloudSources = async () => {
    try {
      const res = await cloudSourceService.listMySources();
      setCloudSources(res.sources || []);
    } catch (error: any) {
      // No sync buttons if the list can't load - not fatal for the page
      logger.error('Failed to load cloud sources:', error);
    }
  };

  const handleTriggerSync = async (src: CloudSourceListRow) => {
    setSyncingSource(src.name);
    try {
      const res = await cloudSourceService.triggerSync(src.name);
      if (res.status === 'error') {
        toast.error(res.message || `Failed to start ${src.provider} sync`);
      } else {
        toast.success(`${src.provider} sync started`, res.message ? { description: res.message } : undefined);
      }
    } catch (error: any) {
      toast.error(getFriendlyErrorMessage(error, `Failed to start ${src.provider} sync`));
    } finally {
      setSyncingSource(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadDocuments(), loadCloudSources()]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleRowClick = (documentId: string) => {
    setSelectedDocumentId(documentId);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedDocumentId(null);
  };

  const handleModalSuccess = () => {
    loadDocuments();
  };

  const handleDeleteConfirm = (documentId: string) => {
    // Block individual deletes while a bulk-delete job is in flight: deleting a
    // doc that's also in the running batch races the worker on the same row,
    // which surfaces as a misleading "not found"/"failed" result even though
    // the delete succeeded. The per-row triggers are also visually disabled;
    // this is the defense-in-depth guard.
    if (bulkDeleting) return;
    setDeleteDocumentId(documentId);
    setDeleteConfirmVisible(true);
  };

  const handleDelete = async () => {
    if (!deleteDocumentId) return;

    setDeleting(true);
    try {
      const result = await knowledgeService.deleteDocument(deleteDocumentId);
      if (result.status === 'archived') {
        toast.success('Document archived', {
          description: 'Golden sample protected - moved to archived status instead of deleted.',
        });
      } else {
        toast.success('Document deleted successfully', {
          description: `Cleanup: PostgreSQL ${result.cleanup.postgresql ? '✓' : '✗'}, Qdrant ${result.cleanup.qdrant ? '✓' : '✗'}, Neo4j ${result.cleanup.neo4j ? '✓' : '✗'}`,
        });
      }
      setDeleteConfirmVisible(false);
      setDeleteDocumentId(null);
      // Optimistically remove the deleted doc from local state
      setDocuments(prev => prev.filter(doc => doc.document_id !== deleteDocumentId));
      setTotalDocuments(prev => prev - 1);
      // Silently refresh in the background (no loading spinner)
      loadDocuments(true);
    } catch (error: any) {
      logger.error('Failed to delete document:', error);
      const errorMessage = extractFrappeError(error, 'Failed to delete document');
      toast.error('Unable to delete', {
        description: errorMessage,
      });
    } finally {
      setDeleting(false);
    }
  };


  // Poll a bulk delete job until it finishes, then refresh the list
  const pollBulkDeleteJob = (jobId: string, toastId: string | number) => {
    const poll = async () => {
      try {
        const status = await documentService.getBulkDeleteStatus(jobId);
        if (status.status === 'finished') {
          await loadDocuments();
          setBulkDeleting(false);
          toast.success('Bulk delete completed', { id: toastId });
          return;
        }
        if (status.status === 'failed') {
          await loadDocuments();
          setBulkDeleting(false);
          toast.error('Bulk delete failed', { id: toastId, description: status.error || 'Background job failed' });
          return;
        }
        // Still running - poll again in 2 seconds
        setTimeout(poll, 2000);
      } catch {
        // Status endpoint failed - fall back to a single delayed refresh
        setTimeout(async () => {
          await loadDocuments();
          setBulkDeleting(false);
          toast.dismiss(toastId);
        }, 3000);
      }
    };
    // Start polling after a short initial delay
    setTimeout(poll, 2000);
  };

  // Bulk delete handler - sync for ≤10 docs, background job for >10
  const SYNC_DELETE_THRESHOLD = 5;

  const handleBulkDelete = async () => {
    if (selectedDocIds.size === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedDocIds) as string[];

    try {
      setBulkDeleteConfirmVisible(false);
      setSelectedDocIds(new Set());

      if (ids.length <= SYNC_DELETE_THRESHOLD) {
        // Small batch - delete synchronously one by one with progress
        let deleted = 0;
        let archived = 0;
        const failures: Array<{ id: string; reason: string }> = [];
        const toastId = toast.loading(`Processing 0/${ids.length} document(s)...`);
        for (const id of ids) {
          try {
            const res = await knowledgeService.deleteDocument(id);
            if (res?.status === 'archived') {
              archived++;
            } else {
              deleted++;
            }
            toast.loading(`Processing ${deleted + archived}/${ids.length} document(s)...`, { id: toastId });
          } catch (err) {
            logger.error(`Failed to delete document ${id}:`, err);
            failures.push({ id, reason: extractFrappeError(err, 'Delete failed') });
          }
        }
        const parts: string[] = [];
        if (deleted) parts.push(`${deleted} deleted`);
        if (archived) parts.push(`${archived} archived (golden sample)`);
        if (failures.length === 0) {
          toast.success(parts.join(', ') || 'No documents processed', { id: toastId });
        } else if (deleted === 0 && archived === 0) {
          toast.error(`Could not process ${failures.length} document(s)`, {
            id: toastId,
            description: failures[0].reason,
          });
        } else {
          toast.warning(`${parts.join(', ')}, ${failures.length} skipped`, {
            id: toastId,
            description: failures[0].reason,
          });
        }
        await loadDocuments();
        setBulkDeleting(false);
        return;
      } else {
        // Large batch - use background job + polling
        const result = await documentService.bulkDeleteUnapproved(ids);
        const bgToastId = toast.loading(`Deleting ${result.total_documents} document(s)...`);
        pollBulkDeleteJob(result.job_id, bgToastId);
        return; // Don't setBulkDeleting(false) - polling will do it
      }
    } catch (error: any) {
      logger.error('Bulk delete failed:', error);
      toast.error('Bulk delete failed', {
        description: error?.message || 'Failed to delete selected documents',
      });
      setBulkDeleting(false);
    }
  };

  // Selection helpers
  const allSelected = documents.length > 0 && documents.every(doc => selectedDocIds.has(doc.document_id));
  const someSelected = selectedDocIds.size > 0 && !allSelected;
  const selectedInKBCount = documents.filter(doc => selectedDocIds.has(doc.document_id) && doc.kb_document_name).length;
 
  const selectedCloudFileNames = documents
    .filter(doc => selectedDocIds.has(doc.document_id) && isCloudSourceDoc(doc))
    .map(doc => doc.file_name);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(documents.map(doc => doc.document_id)));
    }
  };

  const toggleSelectDoc = (docId: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  // Bulk action: Add to KB
  const handleBulkAddToKB = async (folderId: string, folderName: string) => {
    const cloudIds = new Set(
      documents
        .filter(doc => selectedDocIds.has(doc.document_id) && isCloudSourceDoc(doc))
        .map(doc => doc.document_id)
    );
    const skippedNames = documents
      .filter(doc => cloudIds.has(doc.document_id))
      .map(doc => doc.file_name);
    const idsToLink = Array.from(selectedDocIds).filter(id => !cloudIds.has(id));

    if (idsToLink.length === 0) {
      toast.warning('Nothing added to Knowledge Base', {
        description: `All ${cloudIds.size} selected file(s) are cloud-source (Google Drive / SharePoint) and can't be added.`,
      });
      setAddToKBDialogOpen(false);
      return;
    }

    const skippedSuffix = skippedNames.length
      ? ` · skipped ${skippedNames.length} cloud file(s)`
      : '';

    setBulkLinking(true);
    try {
      const result = await knowledgeService.bulkLinkDocumentsToKB(
        idsToLink,
        folderId
      );
      const linkedCount = result.linked ?? idsToLink.length;
      toast.success(`Linked ${linkedCount} document(s) to "${folderName}"`, {
        description:
          (result.failed > 0 ? `${result.failed} failed` : '') + skippedSuffix || undefined,
      });

      // Instant local state update - only the docs we actually linked.
      const linkedIds = new Set(idsToLink);
      const newlyLinked = documents.filter(doc => linkedIds.has(doc.document_id) && !doc.kb_document_name).length;

      // All three cards reflect the filtered view, so the optimistic math is
      // branch-aware. Under the "Hidden from Users" filter the linked docs leave
      // the visible set entirely (Total + Hidden drop; In KB stays 0). With no
      // KB filter they stay in view and just switch bucket (Total unchanged; In
      // KB up, Hidden down). Under "In Knowledge Base" they're already linked,
      // so newlyLinked is 0 and nothing moves.
      if (kbCategorized === 'false') {
        const remaining = documents.filter(doc => !linkedIds.has(doc.document_id));
        setDocuments(remaining);
        setTotalDocuments(prev => prev - linkedIds.size);
        setLibraryStats(prev => ({
          ...prev,
          totalFiles: Math.max(0, prev.totalFiles - newlyLinked),
          notInKnowledgeBase: Math.max(0, prev.notInKnowledgeBase - newlyLinked),
        }));
        if (remaining.length === 0 && currentPage > 1) {
          setCurrentPage(prev => prev - 1);
        } else if (remaining.length === 0) {
          loadDocuments(true);
        }
      } else {
        setDocuments(prev => prev.map(doc =>
          linkedIds.has(doc.document_id)
            ? { ...doc, kb_document_name: folderName, knowledge_base: folderId }
            : doc
        ));
        setLibraryStats(prev => ({
          ...prev,
          inKnowledgeBase: Math.max(0, prev.inKnowledgeBase + newlyLinked),
          notInKnowledgeBase: Math.max(0, prev.notInKnowledgeBase - newlyLinked),
        }));
      }

      setSelectedDocIds(new Set());
      setAddToKBDialogOpen(false);
    } catch (error: unknown) {
      logger.error('Bulk link failed:', error);
      toast.error('Failed to link documents', {
        description: getFriendlyErrorMessage(error, 'Could not link the selected documents.'),
      });
    } finally {
      setBulkLinking(false);
    }
  };

  // Bulk action: Remove from KB
  const handleBulkRemoveFromKB = async () => {
    // Only send docs that are actually in a KB
    const docsInKB = documents
      .filter(doc => selectedDocIds.has(doc.document_id) && doc.kb_document_name)
      .map(doc => doc.document_id);
    if (docsInKB.length === 0) return;

    setBulkUnlinking(true);
    try {
      const result = await knowledgeService.bulkUnlinkDocumentsFromKB(docsInKB);
      const unlinkedCount = result.unlinked ?? docsInKB.length;
      toast.success(`Removed ${unlinkedCount} document(s) from Knowledge Base`, {
        description: result.failed > 0 ? `${result.failed} failed` : undefined,
      });

      // Instant local state update
      const unlinkedIds = new Set(docsInKB);
      const removedCount = docsInKB.length;

      // Branch-aware optimistic math (all cards reflect the filtered view).
      // Under "In Knowledge Base" the unlinked docs leave the visible set (Total
      // + In KB drop; Hidden stays 0). With no KB filter they stay in view and
      // switch bucket (Total unchanged; In KB down, Hidden up). Under "Hidden
      // from Users" they aren't in a KB, so docsInKB is empty (early-returned).
      if (kbCategorized === 'true') {
        const remaining = documents.filter(doc => !unlinkedIds.has(doc.document_id));
        setDocuments(remaining);
        setTotalDocuments(prev => prev - removedCount);
        setLibraryStats(prev => ({
          ...prev,
          totalFiles: Math.max(0, prev.totalFiles - removedCount),
          inKnowledgeBase: Math.max(0, prev.inKnowledgeBase - removedCount),
        }));
        if (remaining.length === 0 && currentPage > 1) {
          setCurrentPage(prev => prev - 1);
        } else if (remaining.length === 0) {
          loadDocuments(true);
        }
      } else {
        setDocuments(prev => prev.map(doc =>
          unlinkedIds.has(doc.document_id)
            ? { ...doc, kb_document_name: null, knowledge_base: null }
            : doc
        ));
        setLibraryStats(prev => ({
          ...prev,
          inKnowledgeBase: Math.max(0, prev.inKnowledgeBase - removedCount),
          notInKnowledgeBase: prev.notInKnowledgeBase + removedCount,
        }));
      }

      setSelectedDocIds(new Set());
    } catch (error: any) {
      logger.error('Bulk unlink failed:', error);
      toast.error('Failed to remove documents from KB', {
        description: error?.message || 'Unknown error',
      });
    } finally {
      setBulkUnlinking(false);
    }
  };

  const sortedDocuments = [...documents].sort((a, b) => {
    if (!sortColumn) return 0;

    if (sortColumn === 'file_name') {
      return sortDirection === 'asc'
        ? a.file_name.localeCompare(b.file_name)
        : b.file_name.localeCompare(a.file_name);
    }

    if (sortColumn === 'file_size') {
      return sortDirection === 'asc'
        ? a.file_size - b.file_size
        : b.file_size - a.file_size;
    }

    if (sortColumn === 'entity_count') {
      const aVal = a.entity_count || 0;
      const bVal = b.entity_count || 0;
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }

    if (sortColumn === 'approved_at') {
      const aDate = new Date(a.approved_at || 0).getTime();
      const bDate = new Date(b.approved_at || 0).getTime();
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    }

    return 0;
  });

  const totalPages = Math.ceil(totalDocuments / pageSize);

  const connectedSources = cloudSources.filter(
    src => src.enabled && src.connection_status === 'Connected'
  );

  return (
    <div className="dark bg-background text-foreground min-h-full space-y-6 max-w-full px-8 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2">
            <Database className="h-7 w-7 text-primary" />
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Document Library</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            View and manage approved documents in the knowledge base. Click on a document to view and edit entity types.
          </p>
        </div>
        {/* shrink-0 keeps the actions on one row: the description text reflows
            instead of squeezing the buttons into a wrap. */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {connectedSources.map((src) => (
            <Button
              key={src.name}
              variant="outline"
              size="sm"
              title={src.source_name}
              onClick={() => handleTriggerSync(src)}
              disabled={syncingSource === src.name}
            >
              {syncingSource === src.name
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <RefreshCw className="h-4 w-4 mr-2" />}
              Sync {src.provider}
            </Button>
          ))}
          {connectedSources.length === 0 && (
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/admin/upload')}>
              <CloudUpload className="h-4 w-4 mr-2" />
              Ingest
            </Button>
          )}
          <RefreshButton onClick={handleRefresh} loading={refreshing} />
        </div>
      </div>

      {/* File Library Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-card/40">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Total Assets</p>
              <span className="p-1.5 rounded-md border border-border bg-muted/40">
                <Files className="h-3.5 w-3.5 text-muted-foreground" />
              </span>
            </div>
            {statsLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <p className="text-3xl font-bold">{libraryStats.totalFiles}</p>
            )}
          </CardContent>
        </Card>
        <Card className="bg-card/40">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Visible to Users</p>
              <span className="p-1.5 rounded-md border border-border bg-muted/40">
                <FileCheck className="h-3.5 w-3.5 text-emerald-400" />
              </span>
            </div>
            {statsLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <p className="text-3xl font-bold text-emerald-400">{libraryStats.inKnowledgeBase}</p>
            )}
          </CardContent>
        </Card>
        <Card className="bg-card/40">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Invisible to Users</p>
              <span className="p-1.5 rounded-md border border-border bg-muted/40">
                <EyeOff className="h-3.5 w-3.5 text-amber-400" />
              </span>
            </div>
            {statsLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <p className="text-3xl font-bold text-amber-400">{libraryStats.notInKnowledgeBase}</p>
            )}
          </CardContent>
        </Card>
        <Card className="bg-card/40">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Storage</p>
              <span className="p-1.5 rounded-md border border-border bg-muted/40">
                <HardDrive className="h-3.5 w-3.5 text-blue-400" />
              </span>
            </div>
            {statsLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <>
                <p className="text-3xl font-bold">{formatFileSize(totalSize)}</p>
                <p className="text-xs text-muted-foreground">across {libraryStats.totalFiles} files</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <KBSearchAndFilters
        filters={filters}
        dateRangeLabel="Approved Date Range"
        showFileSizeFilter={false}
        showKBCategorizedFilter={true}
        portalClassName="dark"
        actions={
          selectedDocIds.size > 0 ? (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3 sm:ml-auto">
              <span className="text-sm font-medium text-muted-foreground">
                {selectedDocIds.size} selected
              </span>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => setAddToKBDialogOpen(true)}
                  disabled={bulkLinking}
                >
                  {bulkLinking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DatabaseZap className="h-4 w-4 mr-2" />}
                  Add to KB
                </Button>
                {selectedInKBCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRemoveFromKBConfirmVisible(true)}
                    disabled={bulkUnlinking}
                  >
                    {bulkUnlinking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlink className="h-4 w-4 mr-2" />}
                    Remove from KB ({selectedInKBCount})
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setBulkDeleteConfirmVisible(true)}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Delete ({selectedDocIds.size})
                </Button>
              </div>
            </div>
          ) : undefined
        }
      />

      {/* Quick Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground mr-1">Quick Filters:</span>
        <button
          type="button"
          onClick={() => handleFileTypeChange('all')}
          className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${fileTypeFilter === 'all' ? 'bg-primary/10 border-primary/40 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/50'}`}
        >
          All Assets
        </button>
        {(filteredFileTypeOptions ?? FILE_TYPE_OPTIONS).filter(opt => opt.value !== 'all').map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => handleFileTypeChange(opt.value)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${fileTypeFilter === opt.value ? 'bg-primary/10 border-primary/40 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/50'}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Documents Table / Cards */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
             <div className="flex items-center justify-center py-12">
              <LoadingSpinner message="Loading documents..." fullScreen={false} />
            </div>
          ) : documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No documents found</p>
              <p className="text-muted-foreground">
                {searchQuery ? 'Try a different search term.' : 'Upload and approve documents to see them here.'}
              </p>
            </div>
          ) : (
          <>
            {/* Mobile / tablet card view (< 1280px - covers iPad Pro 12.9" portrait at 1024px) */}
            <div className="xl:hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={allSelected}
                    ref={(el) => {
                      if (el) (el as any).indeterminate = someSelected;
                    }}
                    onCheckedChange={toggleSelectAll}
                    disabled={bulkDeleting}
                  />
                  <span className="text-sm text-muted-foreground">{totalDocuments} documents</span>
                </div>
              </div>

              {/* Document Cards */}
              <div className="divide-y">
                {sortedDocuments.map((doc) => (
                  <div
                    key={doc.document_id}
                    className={`p-4 active:bg-muted/50 transition-colors ${selectedDocIds.has(doc.document_id) ? 'bg-muted/30' : ''}`}
                    onClick={() => handleRowClick(doc.document_id)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedDocIds.has(doc.document_id)}
                          onCheckedChange={() => toggleSelectDoc(doc.document_id)}
                          disabled={bulkDeleting}
                        />
                      </div>

                      {/* File Icon */}
                      <div className="pt-0.5">
                        {getFileIcon(doc.file_type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Filename */}
                        <h3 className="font-medium text-foreground truncate mb-1">
                          {doc.file_name.replace(/\.[^.]+$/, '')}
                        </h3>

                        {/* Hash ID + entity count */}
                        <p className="text-xs text-muted-foreground font-mono mb-2 truncate">
                          ID: {doc.hash_id || '-'} • {doc.entity_count || 0} entities
                        </p>

                        {/* Badges Row */}
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs">
                            {doc.document_type || doc.file_type?.toUpperCase() || 'Unknown'}
                          </Badge>
                          {(() => {
                            // Mirror the desktop "Source" column so mobile
                            // users can tell at a glance whether the row was
                            // uploaded from device, Google Drive, or SharePoint.
                            const src = doc.source || 'Manual upload';
                            if (src === 'Google Drive') {
                              return (
                                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs">
                                  Google Drive
                                </Badge>
                              );
                            }
                            if (src === 'SharePoint') {
                              return (
                                <Badge variant="outline" className="bg-violet-500/10 text-violet-400 border-violet-500/30 text-xs">
                                  SharePoint
                                </Badge>
                              );
                            }
                            return (
                              <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-xs">
                                Manual upload
                              </Badge>
                            );
                          })()}
                          {doc.kb_document_name && (
                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs">
                              {doc.kb_document_name}
                            </Badge>
                          )}
                        </div>

                        {/* Details Row */}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{formatFileSize(doc.file_size)}</span>
                          <span>&bull;</span>
                          <span>{formatDate(doc.approved_at)}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {doc.file_attachment && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={async () => {
                              try {
                                const csrfToken = await getCsrfToken();
                                const headers: Record<string, string> = {
                                  'Content-Type': 'application/json',
                                };
                                if (csrfToken) headers['X-Frappe-CSRF-Token'] = csrfToken;
                                const response = await fetch(getApiEndpoint('REFRESH_S3_URLS'), {
                                  method: 'POST',
                                  headers,
                                  credentials: 'include',
                                  body: JSON.stringify({ data: { url: doc.file_attachment } }),
                                });
                                const result = await response.json();
                                const refreshedUrl = result?.message?.data?.url;
                                window.open(refreshedUrl || doc.file_attachment, '_blank');
                              } catch {
                                window.open(doc.file_attachment, '_blank');
                              }
                            }}
                            aria-label="View or download"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteConfirm(doc.document_id)}
                          disabled={bulkDeleting}
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile Pagination */}
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalDocuments}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                isMobile
              />
            </div>

            {/* Desktop table view (≥ 1280px) */}
            <div className="hidden xl:block">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/30 sticky top-0 z-10">
                    <TableRow className="border-b">
                      <TableHead className="w-10 py-4" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={allSelected}
                          ref={(el) => {
                            if (el) (el as any).indeterminate = someSelected;
                          }}
                          onCheckedChange={toggleSelectAll}
                          disabled={bulkDeleting}
                        />
                      </TableHead>
                      <SortableTableHeader
                        column="file_name"
                        label="Asset Identity"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                      <TableHead className="font-medium text-foreground py-4">Status</TableHead>
                      <TableHead className="font-medium text-foreground py-4">Source</TableHead>
                      <SortableTableHeader
                        column="file_size"
                        label="Size"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        column="approved_at"
                        label="Approved"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedDocuments.map((doc) => (
                      <TableRow
                        key={doc.document_id}
                        className={`cursor-pointer hover:bg-muted/50 ${selectedDocIds.has(doc.document_id) ? 'bg-muted/30' : ''}`}
                        onClick={() => handleRowClick(doc.document_id)}
                      >
                        <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedDocIds.has(doc.document_id)}
                            onCheckedChange={() => toggleSelectDoc(doc.document_id)}
                            disabled={bulkDeleting}
                          />
                        </TableCell>
                        <TableCell className="min-w-[260px] max-w-[360px]">
                          <div className="flex items-center gap-3">
                            <span className="p-2 rounded-lg border border-border bg-muted/40 shrink-0">
                              {getFileIcon(doc.file_type)}
                            </span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0 mb-1.5">
                                <span className="font-medium truncate leading-tight" title={doc.file_name}>
                                  {doc.file_name.replace(/\.[^.]+$/, '')}
                                </span>
                                <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-[10px] shrink-0 leading-normal py-0.5">
                                  {doc.document_type || doc.file_type?.toUpperCase() || 'Unknown'}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground font-mono truncate leading-relaxed">
                                ID: {doc.hash_id || '-'} • {doc.entity_count || 0} Entities Extracted
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {doc.kb_document_name ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="font-mono text-[10px] tracking-wider bg-emerald-500/10 text-emerald-400 border-emerald-500/30 cursor-default">
                                    VISIBLE
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="dark">Shared with users · {doc.kb_document_name}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="font-mono text-[10px] tracking-wider bg-muted text-muted-foreground border-border cursor-default">
                                    INDEXED
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="dark">Indexed but not visible to users</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            // Per-provider badge so the user can tell at a
                            // glance whether a row was uploaded from device,
                            // pulled from Google Drive, or pulled from
                            // SharePoint. Matches the icons used elsewhere
                            // (Cloud=emerald for Drive, FileBox=blue for SP).
                            const src = doc.source || 'Manual upload';
                            if (src === 'Google Drive') {
                              return (
                                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                  Google Drive
                                </Badge>
                              );
                            }
                            if (src === 'SharePoint') {
                              return (
                                <Badge variant="outline" className="bg-violet-500/10 text-violet-400 border-violet-500/30">
                                  SharePoint
                                </Badge>
                              );
                            }
                            return (
                              <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
                                Manual upload
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(doc.approved_at)}
                        </TableCell>
                        <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="dark bg-background text-foreground">
                              {doc.file_attachment && (
                                <DropdownMenuItem
                                  onClick={async () => {
                                    try {
                                      const csrfToken = await getCsrfToken();
                                      const headers: Record<string, string> = {
                                        'Content-Type': 'application/json',
                                      };
                                      if (csrfToken) headers['X-Frappe-CSRF-Token'] = csrfToken;
                                      const response = await fetch(getApiEndpoint('REFRESH_S3_URLS'), {
                                        method: 'POST',
                                        headers,
                                        credentials: 'include',
                                        body: JSON.stringify({ data: { url: doc.file_attachment } }),
                                      });
                                      const result = await response.json();
                                      const refreshedUrl = result?.message?.data?.url;
                                      window.open(refreshedUrl || doc.file_attachment, '_blank');
                                    } catch {
                                      window.open(doc.file_attachment, '_blank');
                                    }
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View / Download
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDeleteConfirm(doc.document_id)}
                                disabled={bulkDeleting}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Desktop Pagination */}
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalDocuments}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
          )}
        </CardContent>
      </Card>

      {/* Document Detail Modal */}
      <ApprovedDocumentModal
        documentId={selectedDocumentId}
        visible={modalVisible}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />

      {/* Add to Knowledge Base Dialog */}
      <AddToKnowledgeBaseDialog
        open={addToKBDialogOpen}
        onOpenChange={setAddToKBDialogOpen}
        selectedCount={selectedDocIds.size}
        skippedCloudFiles={selectedCloudFileNames}
        onConfirm={handleBulkAddToKB}
      />

      {/* Remove from KB Confirmation Dialog */}
      <DeleteConfirmDialog
        open={removeFromKBConfirmVisible}
        onOpenChange={setRemoveFromKBConfirmVisible}
        title="Remove from Knowledge Base"
        description={
          <>
            This will remove {selectedInKBCount} document{selectedInKBCount !== 1 ? 's' : ''} from the Knowledge Base.
            The files will remain in the Document Library but will no longer be linked to any Knowledge Base folder.
          </>
        }
        onConfirm={() => {
          setRemoveFromKBConfirmVisible(false);
          handleBulkRemoveFromKB();
        }}
        loading={bulkUnlinking}
        confirmLabel="Remove"
        contentClassName="dark bg-background text-foreground"
      />

      {/* Delete Confirmation Dialog (single) */}
      <DeleteConfirmDialog
        open={deleteConfirmVisible}
        onOpenChange={setDeleteConfirmVisible}
        title="Delete Document"
        description={
          <>
            This will permanently delete the document from:
            <ul className="list-disc ml-6 mt-2">
              <li>Metadata and entities</li>
              <li>Vector embeddings</li>
              <li>Knowledge graph</li>
            </ul>
            This action cannot be undone.
          </>
        }
        onConfirm={handleDelete}
        loading={deleting}
        contentClassName="dark bg-background text-foreground"
      />

      {/* Bulk Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={bulkDeleteConfirmVisible}
        onOpenChange={setBulkDeleteConfirmVisible}
        title={`Delete ${selectedDocIds.size} Document${selectedDocIds.size !== 1 ? 's' : ''}`}
        description={
          <>
            This will permanently delete <strong>{selectedDocIds.size}</strong> document{selectedDocIds.size !== 1 ? 's' : ''} from:
            <ul className="list-disc ml-6 mt-2">
              <li>Metadata and entities</li>
              <li>Vector embeddings</li>
              <li>Knowledge graph</li>
            </ul>
            Deletion runs in the background. This action cannot be undone.
          </>
        }
        onConfirm={handleBulkDelete}
        loading={bulkDeleting}
        confirmLabel={`Delete ${selectedDocIds.size} Document${selectedDocIds.size !== 1 ? 's' : ''}`}
        contentClassName="dark bg-background text-foreground"
      />
    </div>
  );
};

export default KBDocumentsPage;
